import { useEffect, useState, useCallback } from "react";
import api, { errMsg } from "../api";
import { toast } from "sonner";

const ACTION_LABELS = {
  connexion: "Connexion réussie",
  echec_connexion: "Échec de connexion",
  deconnexion_forcee: "Déconnexion forcée",
  creation_facture: "Création de facture",
  modification_facture: "Modification de facture",
  date_facture: "Changement de date de facture",
  envoi_facture: "Facture marquée envoyée",
  paiement_facture: "Facture marquée payée",
  suppression_facture: "Suppression de facture",
  creation_bon: "Création de bon",
  modification_bon: "Modification de bon",
  suppression_bon: "Suppression de bon",
  archivage_bon: "Archivage de bon",
  creation_client: "Création de client",
  modification_client: "Modification de client",
  suppression_client: "Suppression de client",
  creation_article: "Création d'article (liste de prix)",
  modification_article: "Modification d'article",
  suppression_article: "Suppression d'article",
  creation_utilisateur: "Création d'utilisateur",
  invitation_utilisateur: "Invitation utilisateur",
  lien_mdp_utilisateur: "Lien de mot de passe envoyé",
  changement_role: "Changement de rôle",
  desactivation_utilisateur: "Désactivation d'utilisateur",
  reactivation_utilisateur: "Réactivation d'utilisateur",
  modification_utilisateur: "Modification d'utilisateur",
  suppression_utilisateur: "Suppression d'utilisateur",
  modification_configuration: "Modification de la configuration",
  activation_code_email: "Activation du code par email",
  desactivation_code_email: "Désactivation du code par email",
  export_donnees: "Export des données",
  export_winbiz: "Export Winbiz (bizexdoc.csv)",
};

const PAGE_SIZE = 50;

export default function Journal() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [users, setUsers] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [userId, setUserId] = useState("");
  const [action, setAction] = useState("");
  const [page, setPage] = useState(1);

  const load = useCallback(() => {
    const params = { page, page_size: PAGE_SIZE };
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (userId) params.user_id = userId;
    if (action) params.action = action;
    api.get("/activity", { params }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [page, dateFrom, dateTo, userId, action]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data)).catch(() => {}); }, []);

  const pages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
  const fmtTs = (ts) => {
    const d = new Date(ts);
    return `${d.toLocaleDateString("fr-CH")} ${d.toLocaleTimeString("fr-CH", { hour: "2-digit", minute: "2-digit" })}`;
  };
  const inputCls = "rounded-lg border border-slate-300 px-3 py-1.5 text-sm bg-white";

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5" data-testid="journal-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-xl font-semibold text-slate-900">Journal d'activité</h1>
        <span className="text-sm text-slate-500" data-testid="journal-total">{data.total} événement(s)</span>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 p-4 flex flex-wrap items-end gap-3" data-testid="journal-filters">
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider">Du</label>
          <input data-testid="journal-filter-from" type="date" value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} className={inputCls} />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider">Au</label>
          <input data-testid="journal-filter-to" type="date" value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(1); }} className={inputCls} />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider">Utilisateur</label>
          <select data-testid="journal-filter-user" value={userId}
            onChange={(e) => { setUserId(e.target.value); setPage(1); }} className={inputCls}>
            <option value="">Tous</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider">Type d'action</label>
          <select data-testid="journal-filter-action" value={action}
            onChange={(e) => { setAction(e.target.value); setPage(1); }} className={inputCls}>
            <option value="">Toutes</option>
            {Object.entries(ACTION_LABELS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
        </div>
        <button data-testid="journal-filter-reset"
          onClick={() => { setDateFrom(""); setDateTo(""); setUserId(""); setAction(""); setPage(1); }}
          className="px-3 py-1.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100">
          Réinitialiser
        </button>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-100">
              <th className="px-5 py-3">Date / heure</th>
              <th className="px-5 py-3">Utilisateur</th>
              <th className="px-5 py-3">Action</th>
              <th className="px-5 py-3">Objet</th>
              <th className="px-5 py-3">Détails</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-testid="journal-table">
            {data.items.map((e) => (
              <tr key={e.id} data-testid={`journal-row-${e.id}`} className="hover:bg-slate-50 align-top">
                <td className="px-5 py-3 text-slate-600 whitespace-nowrap">{fmtTs(e.ts)}</td>
                <td className="px-5 py-3">
                  <div className="font-medium text-slate-900">{e.user_name || "—"}</div>
                  <div className="text-xs text-slate-500">{e.user_email}</div>
                </td>
                <td className="px-5 py-3">{ACTION_LABELS[e.action] || e.action}</td>
                <td className="px-5 py-3 text-slate-700">{e.object || "—"}</td>
                <td className="px-5 py-3 text-xs text-slate-500" data-testid={`journal-details-${e.id}`}>
                  {(e.changes || []).map((ch, i) => (
                    <div key={i}>{ch.field} : {ch.old || "—"} → {ch.new || "—"}</div>
                  ))}
                </td>
              </tr>
            ))}
            {data.items.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-400" data-testid="journal-empty">Aucun événement</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between" data-testid="journal-pagination">
        <button data-testid="journal-prev" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
          className="px-4 py-2 rounded-lg text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-40">
          ← Plus récents
        </button>
        <span className="text-sm text-slate-500">Page {page} / {pages}</span>
        <button data-testid="journal-next" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}
          className="px-4 py-2 rounded-lg text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-40">
          Plus anciens →
        </button>
      </div>
    </div>
  );
}
